import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DateInput, Loader } from '../../components';
import { AlertSVG } from '../../components/svgs';
import TransactionLabelField from '../../components/transaction/TransactionLabelField';
import MultiAnimalSpecimenSelector from '../../components/select-specimen/MultiAnimalSpecimenSelector';
import MultiItemStorageLocations, {
  SpecimenQuantities,
} from '../../components/select-item-storage-locations/MultiItemStorageLocations';
import { SelectedSpecimenQuantity } from '../../components/specimen-tables/SelectSpecimens';
import { RequireAlert } from '../inventory-management/InventoryManagementCollectDeposit';
import InsuranceForm, { ShippingSummary } from '../../components/insurance-form/InsuranceForm';
import InsuranceTermsModal from '../transaction-cart/insurance-terms-conditions/InsuranceTermsModal';
import MergeShippingPaymentInfo from './MergeShipmentPaymentInfo';
import { defaultShippingSummary } from '../transaction-cart/TransactionCartWithdraw';
import { BulkTransaction, SpecimenLocation, SpecimenSelection } from '../../types/interfaces';
import { TransactionStatusEnum } from '../../types/enums';
import { getBulkTransactionById } from '../../api/bulkTransactionApi';
import { appendAnimalsToPendingWithdraw, appendAnimalsToProcessedWithdraw } from '../../api/inventoryApi';
import { storeLocationsForProcessedTransaction } from '../../api/inventoryTransactionsApi';
import { showToast } from '../../services/toast.service';
import { centsToDollars } from '../../services/account.service';
import { dateFormatMMDDYYYY } from '../../utils/commonUtils';
import { toastMessages } from '../../constants/errorMessages';
import { ROUTE_PATHS } from '../../constants/routePaths';
import {
  ADDRESS_CONSTANTS,
  BUTTON_CONSTANTS,
  INVENTORY_CONSTANTS,
  LABEL_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
  VALIDATION_ALERT_CONSTANTS,
} from '../../constants/common';
import './updateWithdrawTransaction.scss';

const UpdateWithdrawTransaction = (): JSX.Element => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const [bulkOrderTransaction, setBulkOrderTransaction] = useState<BulkTransaction>();
  const [breedDate, setBreedDate] = useState<Date>();
  const [validate, setValidate] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const [selectedSpecimens, setSelectedSpecimens] = useState<{ valid: boolean; value: SelectedSpecimenQuantity[] }>({
    valid: false,
    value: [],
  });
  const [selectedLocations, setSelectedLocations] = useState<{ specimenId: number; specimenLocations: SpecimenQuantities[] }[]>(
    [],
  );
  const [mergeShippingSummary, setMergeShippingSummary] = useState<ShippingSummary>(defaultShippingSummary);
  const [shippingInsuranceValid, setShippingInsuranceValid] = useState<boolean>(false);
  const [isPaymentPage, setIsPaymentPage] = useState<boolean>(false);
  const [insuranceTermsModalOpen, setInsuranceTermsModalOpen] = useState(false);
  const [updatingTransaction, setUpdatingTransaction] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);

  const specimenHeaders: string[] = [
    TABLE_HEADER_CONSTANTS.ANIMAL,
    TABLE_HEADER_CONSTANTS.LOT_DATE_NO,
    TABLE_HEADER_CONSTANTS.QTY_TO_WITHDRAW,
  ];

  const insuranceAmount = useMemo(() => {
    return centsToDollars(
      bulkOrderTransaction?.shippingPaymentTransaction.reduce((sum: any, txn: any) => sum + (txn?.insuredAmountCents ?? 0), 0) ??
        0,
    );
  }, [bulkOrderTransaction]);

  const insurancePremium = useMemo(() => {
    return centsToDollars(
      bulkOrderTransaction?.shippingPaymentTransaction.reduce(
        (sum: any, txn: any) => sum + (txn?.insurancePremiumCents ?? 0),
        0,
      ) ?? 0,
    );
  }, [bulkOrderTransaction]);

  const totalShippingAmount = useMemo(() => {
    return (
      mergeShippingSummary.insurance +
      mergeShippingSummary.handlingProcessingFee +
      mergeShippingSummary.tankRentalFee +
      mergeShippingSummary.shippingOutBoundCost +
      mergeShippingSummary.shippingReturnCost
    );
  }, [mergeShippingSummary]);

  useEffect(() => {
    fetchBulkTransaction();
  }, [transactionId]);

  useEffect(() => {
    if (breedDate) {
      setNotes(updateBreedDateInNotes(notes, dateFormatMMDDYYYY(breedDate)));
    }
  }, [breedDate]);

  const fetchBulkTransaction = async () => {
    setIsLoading(true);
    try {
      const params = {
        include:
          'Account,inventoryTransactions,inventoryTransactions.Specimen.Animal,ShippingOrderDetail.ShippingLabelInformation,ShippingPaymentTransaction',
      };
      const { data: bulkInventoryTransaction } = await getBulkTransactionById(+transactionId!, params);
      setBulkOrderTransaction(bulkInventoryTransaction);
      setBreedDate(new Date(bulkInventoryTransaction.breedDate));
      setNotes(bulkInventoryTransaction.notes || '');
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  };

  const updateBreedDateInNotes = (existingNotes: string, newDate: string): string => {
    const lines = existingNotes.split('\n');
    const updatedLines = lines.map(line => (line.startsWith('Breed Date:') ? `Breed Date: ${newDate}` : line));

    if (!updatedLines.some(line => line.startsWith('Breed Date:'))) {
      updatedLines.push(`Breed Date: ${newDate}`);
    }

    return updatedLines.join('\n');
  };

  const checkValidity = () => {
    return breedDate != undefined && breedDate && shippingInsuranceValid;
  };

  const isQuantityValid = (showToasts?: boolean) => {
    const allZero = selectedSpecimens.value.every(inventoryLot => inventoryLot.quantity === 0);
    const overQty = selectedSpecimens.value.some(cartItem => cartItem.quantity > cartItem?.availableQuantity);

    if (allZero) {
      showToasts && showToast.error(toastMessages.INVALID_ZERO_QUANTITY);
      return false;
    } else if (overQty) {
      showToasts && showToast.error(toastMessages.CANT_WITHDRAW_MORE_THAN_AVAIL_QTY);
      return false;
    } else if (selectedSpecimens.valid) {
      return true;
    } else {
      return false;
    }
  };

  const hasInvalidSpecimenSelections = (): boolean => {
    return selectedSpecimens.value.some(specimen => {
      if (!specimen.selected) return false;

      const specimenLocationEntry = selectedLocations.find(location => location.specimenId === specimen.specimenId);
      if (!specimenLocationEntry) return true;

      const totalSelectedQty = specimenLocationEntry.specimenLocations
        .filter(loc => loc.selected)
        .reduce((sum, loc) => sum + loc.quantity, 0);

      return totalSelectedQty !== specimen.quantity;
    });
  };

  const handleProceedToPayment = async () => {
    setValidate(true);

    if (
      checkValidity() &&
      isQuantityValid(true) &&
      (bulkOrderTransaction?.status === TransactionStatusEnum.Processed ? !hasInvalidSpecimenSelections() : true)
    ) {
      if (mergeShippingSummary?.shippingInsurance === LABEL_CONSTANTS.YES) {
        mergeShippingSummary.termsAndConditions === LABEL_CONSTANTS.ACCEPTED
          ? setIsPaymentPage(true)
          : setInsuranceTermsModalOpen(true);
      } else {
        setMergeShippingSummary(prevSummary => ({ ...prevSummary, termsAndConditions: LABEL_CONSTANTS.DECLINED }));
        setIsPaymentPage(true);
      }
    }
  };

  const handleMergeOrder = async () => {
    if (!updatingTransaction) return;

    setUpdatingTransaction(false);
    setValidate(true);

    try {
      if (
        checkValidity() &&
        isQuantityValid(true) &&
        (bulkOrderTransaction?.status === TransactionStatusEnum.Processed ? !hasInvalidSpecimenSelections() : true)
      ) {
        const mappedDiscardSpecimens: SpecimenSelection[] = selectedSpecimens.value.map(item => {
          return { specimenId: item.specimenId, quantity: item.quantity };
        });
        const commonPayload = { animalSelections: [{ specimens: mappedDiscardSpecimens }], breedDate: breedDate!, notes: notes };
        const accountId = bulkOrderTransaction?.accountId!;
        const bulkOrderId = bulkOrderTransaction?.bulkOrderId!;

        if (bulkOrderTransaction?.status === TransactionStatusEnum.Pending) {
          await appendAnimalsToPendingWithdraw(accountId, bulkOrderId, commonPayload);
        } else if (bulkOrderTransaction?.status === TransactionStatusEnum.Processed) {
          await appendAnimalsToProcessedWithdraw(accountId, bulkOrderId, mergeShippingSummary.insurance > 0, commonPayload);
          const updatedLocations = selectedLocations.map(location => mapInventoryStorageLocations(location));
          await storeLocationsForProcessedTransaction(bulkOrderTransaction?.bulkOrderId!, updatedLocations);
        }
        showToast.success(toastMessages.SUCCESS_UPDATED_WITHDRAW_TRANSACTION);
        navigate(state.returnLocation);
      }
    } catch (error: any) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setUpdatingTransaction(true);
    }
  };

  const mapInventoryStorageLocations = (specimenLocation: { specimenId: number; specimenLocations: SpecimenQuantities[] }) => {
    const specimenLocations: SpecimenLocation[] = [];

    specimenLocation.specimenLocations?.forEach(location => {
      if (location.selected && location.quantity > 0) {
        specimenLocations.push({
          specimenLocationId: location.specimenLocationId!,
          storageCanisterId: location.selectedCanisterId!,
          quantity: location.quantity,
          transactionLinkId: location.transactionLinkId,
          specimenId: location.specimenId,
        });
      }
    });

    return { specimenId: specimenLocation.specimenId, specimenLocations };
  };

  const handleConfirm = async () => {
    setMergeShippingSummary({ ...mergeShippingSummary, termsAndConditions: LABEL_CONSTANTS.ACCEPTED });
    setInsuranceTermsModalOpen(false);
    setIsPaymentPage(true);
  };

  const isAnySpecimenQuantityExceeded = (): boolean => {
    return selectedSpecimens.value.some(specimen => {
      if (!specimen.selected) return false;
      const specimenLocationEntry = selectedLocations.find(location => location.specimenId === specimen.specimenId);
      if (!specimenLocationEntry) return false;

      const totalSelectedQty = specimenLocationEntry.specimenLocations
        .filter(loc => loc.selected)
        .reduce((sum, loc) => sum + loc.quantity, 0);

      return totalSelectedQty != undefined && totalSelectedQty > specimen.quantity;
    });
  };

  return (
    <div>
      {isLoading && <Loader loaderSize={'medium'} pageLoader />}
      {!isLoading && (
        <div className="inventory-action card">
          <h1>{PAGE_HEADER_CONSTANTS.UPDATE_EXISTING_WITHDRAW}</h1>
          {!isPaymentPage ? (
            <div className="update-withdraw-transaction">
              <TransactionLabelField label={`${LABEL_CONSTANTS.BULK_TRANSACTION_ID}:`} value={transactionId} />
              <TransactionLabelField label={`${LABEL_CONSTANTS.STATUS}:`} value={bulkOrderTransaction?.status} />
              <TransactionLabelField
                label={`${LABEL_CONSTANTS.ACCOUNT_OWNER_ID}:`}
                value={`${bulkOrderTransaction?.account?.accountId}-${bulkOrderTransaction?.account?.name}`}
              />
              <div className="form-row">
                <label>
                  *{LABEL_CONSTANTS.BREED_DATE}:
                  <RequireAlert isActive={validate && !breedDate} />
                </label>
                <DateInput value={breedDate} onChange={setBreedDate} isClearable minDate={new Date()} maxDate={null} />
              </div>
              <div className="select-animal">
                <h3>{LABEL_CONSTANTS.SPECIMENS}</h3>
                <div className="specimen-table">
                  <table>
                    <thead>
                      <tr>
                        {specimenHeaders.map((header, index) => (
                          <th key={`${header}-${index}`}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bulkOrderTransaction?.inventoryTransactions?.map((transaction, index) => {
                        const animalDetails = `${transaction.specimen?.animal?.code} - ${transaction.specimen?.animal?.name}`;
                        const freezeDateText = transaction?.specimen?.freezeDate
                          ? `${dateFormatMMDDYYYY(transaction?.specimen?.freezeDate)} - ${transaction?.specimen?.specimenId}`
                          : '';

                        return (
                          <tr key={`specimen-row-${transaction.specimen?.specimenId}-${index}`}>
                            <td>{animalDetails}</td>
                            <td>{freezeDateText}</td>
                            <td>{String(Math.abs(transaction.changeQuantity!))}</td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td colSpan={3}>&nbsp;</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <TransactionLabelField
                label={`${ADDRESS_CONSTANTS.SHIPPING_ADDRESS}:`}
                value={[
                  bulkOrderTransaction?.shippingOrderDetail?.shipToAddressLine,
                  bulkOrderTransaction?.shippingOrderDetail?.shipToCity,
                  bulkOrderTransaction?.shippingOrderDetail?.shipToState,
                  bulkOrderTransaction?.shippingOrderDetail?.shipToPostalCode,
                  bulkOrderTransaction?.shippingOrderDetail?.shipToCountry,
                  bulkOrderTransaction?.shippingOrderDetail?.phoneNumber,
                ]
                  .filter(Boolean)
                  .join(', ')}
              />
              <TransactionLabelField label={`${LABEL_CONSTANTS.INSURANCE_AMOUNT}:`} value={`$${insuranceAmount}`} />
              <TransactionLabelField label={`${LABEL_CONSTANTS.INSURANCE_PREMIUM}:`} value={`$${insurancePremium}`} />
              {!updatingTransaction && <Loader loaderSize={'medium'} />}

              <MultiAnimalSpecimenSelector accountId={bulkOrderTransaction?.accountId!} onChange={e => setSelectedSpecimens(e)} />

              {bulkOrderTransaction?.status === TransactionStatusEnum.Processed && (
                <>
                  <MultiItemStorageLocations
                    selectedSpecimens={selectedSpecimens.value}
                    onLocationsChange={setSelectedLocations}
                  />
                  {isAnySpecimenQuantityExceeded() && (
                    <div className="validated-input-message-error margin-top">
                      <AlertSVG />
                      {VALIDATION_ALERT_CONSTANTS.WITHDRAW_QTY_EXCEEDED}
                    </div>
                  )}
                </>
              )}

              <InsuranceForm
                validate={validate}
                setValid={setShippingInsuranceValid}
                shippingSummary={mergeShippingSummary}
                setShippingSummary={setMergeShippingSummary}
                promptLabel={
                  Number(insuranceAmount) > 0
                    ? INVENTORY_CONSTANTS.UPDATE_INSURANCE_PROMPT
                    : INVENTORY_CONSTANTS.ADD_INSURANCE_PROMPT
                }
                existingInsurance={Number(insuranceAmount) > 0}
              />
              <div className="buttons">
                <button type="button" className="button red inverted medium" onClick={() => navigate(state.returnLocation)}>
                  {BUTTON_CONSTANTS.CANCEL}
                </button>
                <button
                  type="submit"
                  className={`button green ${
                    checkValidity() &&
                    isQuantityValid() &&
                    (bulkOrderTransaction?.status === TransactionStatusEnum.Processed ? !hasInvalidSpecimenSelections() : true)
                      ? ''
                      : 'disabled'
                  }`}
                  onClick={
                    mergeShippingSummary.shippingInsurance === LABEL_CONSTANTS.YES ? handleProceedToPayment : handleMergeOrder
                  }>
                  {mergeShippingSummary.shippingInsurance === LABEL_CONSTANTS.YES
                    ? BUTTON_CONSTANTS.PROCEED_TO_PAYMENT
                    : BUTTON_CONSTANTS.SUBMIT}
                </button>
              </div>
            </div>
          ) : (
            <MergeShippingPaymentInfo
              setIsPaymentPage={setIsPaymentPage}
              shippingSummary={mergeShippingSummary}
              totalShippingAmount={totalShippingAmount}
              bulkTransaction={bulkOrderTransaction}
              handleMergeOrder={handleMergeOrder}
            />
          )}
          <InsuranceTermsModal
            isOpen={insuranceTermsModalOpen}
            onClose={() => setInsuranceTermsModalOpen(false)}
            onAccept={() => handleConfirm()}
          />
        </div>
      )}
    </div>
  );
};

export default UpdateWithdrawTransaction;
